import { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import './myaccount.css';
import apiProvider from '../../apiProvider/api';
import { toast } from 'react-toastify';

import { Helmet } from 'react-helmet-async';
import { useTranslation } from "../../context/TranslationContext";

const SignupSchema = Yup.object().shape({
    userName: Yup.string()
        .matches(/^[a-zA-Z\s]+$/, 'User name should only contain alphabets')
        .required('User name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    mobileNumber: Yup.string()
        .matches(/^[0-9]{10}$/, 'Mobile number must be exactly 10 digits')
        .required('Mobile number is required'),
    password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .matches(/[A-Z]/, "Must contain at least one uppercase letter")
        .matches(/[a-z]/, "Must contain at least one lowercase letter")
        .matches(/[0-9]/, "Must contain at least one number")
        .matches(/[@$!%*?&]/, "Must contain at least one special character (@$!%*?&)")
        .required("Password is required"),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Confirm Password is required'),
    acceptTerms: Yup.boolean()
        .oneOf([true], 'You must accept the terms and conditions')
        .required('You must accept the terms and conditions'),
});

const RegisterPage = () => {

    const navigate = useNavigate();
    const { translateSync, currentLanguage, setCurrentLanguage } = useTranslation();

    const formSubmit = async (value) => {
        try {
            const input = {
                name: value.userName,
                email: value.email,
                phone: value.mobileNumber,
                password: value.password,
                // confirmPassword: value.confirmPassword // Backend doesn't need this
            }
            console.log(input, "input");
            const result = await apiProvider.register(input)
            console.log(result, "result-register");

            // Handle both 200 and 201 success status codes
            if (result && (result.status === 200 || result.status === 201)) {
                // Toast is handled within apiProvider.
                // Navigate to login page.
                navigate("/login");
            } else {
                const errorMsg = result?.response?.response?.data?.message || "Registration failed. Please try again.";
                toast.error(errorMsg, {
                    autoClose: 3000,
                    style: { fontFamily: "'Lato', sans-serif" }
                });
            }

        } catch (error) {
            console.error("Registration error:", error);
        }
    }
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    return (
        <>
            <Helmet>
                <title>Sign Up | Colours Boutique</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="auth-page-wrapper" style={{
                backgroundColor: "#ebe7e1",
                backgroundImage: "url('https://www.transparenttextures.com/patterns/natural-paper.png')",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                paddingTop: "160px",
                paddingBottom: "50px",
                fontFamily: "'Lato', sans-serif"
            }}>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-6 col-md-8">
                            <div className="bg-white p-5 shadow-sm border border-light" style={{ position: "relative" }}>
                                {/* Decorative Border Top */}
                                <div style={{
                                    position: "absolute",
                                    top: "0",
                                    left: "0",
                                    right: "0",
                                    height: "4px",
                                    background: "linear-gradient(to right, #d4af37, #800000)"
                                }}></div>

                                <div className="text-center mb-5">
                                    <h2 className="fw-bold text-uppercase" style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "2px", color: "#3a3530" }}>
                                        {translateSync('Create Account')}
                                    </h2>
                                    <p className="text-muted small text-uppercase" style={{ letterSpacing: "1px" }}>
                                        {translateSync('Join our boutique community')}
                                    </p>
                                </div>

                                <Formik
                                    initialValues={{
                                        userName: '',
                                        email: '',
                                        mobileNumber: '',
                                        password: '',
                                        confirmPassword: '',
                                        acceptTerms: false
                                    }}
                                    validationSchema={SignupSchema}
                                    onSubmit={values => {
                                        formSubmit(values)
                                    }}
                                >
                                    {({ errors, touched, handleChange, handleBlur, values }) => (
                                        <Form >
                                            <div className="row g-3">
                                                <div className="col-12">
                                                    <label className="bs-form-label small text-uppercase fw-bold mb-1 text-muted" style={{ fontSize: "0.75rem" }}>{translateSync('Username')}</label>
                                                    <input
                                                        className='form-control rounded-0 py-2 px-3'
                                                        name="userName"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder={translateSync('Choose a username')}
                                                        type="text"
                                                        style={{ border: "1px solid #ddd", fontSize: "0.9rem" }}
                                                        onKeyPress={(event) => {
                                                            if (!/[a-zA-Z\s]/.test(event.key)) {
                                                                event.preventDefault();
                                                            }
                                                        }}
                                                    />
                                                    {errors.userName && touched.userName && <div className="text-danger small mt-1 text-start">{errors.userName}</div>}
                                                </div>

                                                <div className="col-12">
                                                    <label className="bs-form-label small text-uppercase fw-bold mb-1 text-muted" style={{ fontSize: "0.75rem" }}>{translateSync('Email Address')}</label>
                                                    <input
                                                        className='form-control rounded-0 py-2 px-3'
                                                        name="email"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder={translateSync('Enter your email')}
                                                        type="text"
                                                        style={{ border: "1px solid #ddd", fontSize: "0.9rem" }}
                                                    />
                                                    {errors.email && touched.email && <div className="text-danger small mt-1 text-start">{errors.email}</div>}
                                                </div>

                                                <div className="col-12">
                                                    <label className="bs-form-label small text-uppercase fw-bold mb-1 text-muted" style={{ fontSize: "0.75rem" }}>{translateSync('Mobile Number')}</label>
                                                    <input
                                                        className='form-control rounded-0 py-2 px-3'
                                                        name="mobileNumber"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder={translateSync('Enter mobile number')}
                                                        type="text"
                                                        style={{ border: "1px solid #ddd", fontSize: "0.9rem" }}
                                                        maxLength={10}
                                                        onKeyPress={(event) => {
                                                            if (!/[0-9]/.test(event.key)) {
                                                                event.preventDefault();
                                                            }
                                                        }}
                                                    />
                                                    {errors.mobileNumber && touched.mobileNumber && <div className="text-danger small mt-1 text-start">{errors.mobileNumber}</div>}
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="bs-form-label small text-uppercase fw-bold mb-1 text-muted" style={{ fontSize: "0.75rem" }}>{translateSync('Password')}</label>
                                                    <input
                                                        className='form-control rounded-0 py-2 px-3'
                                                        name="password"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder={translateSync("Create password")}
                                                        type="password"
                                                        style={{ border: "1px solid #ddd", fontSize: "0.9rem" }}
                                                    />
                                                    {errors.password && touched.password && <div className="text-danger small mt-1 text-start">{errors.password}</div>}
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="bs-form-label small text-uppercase fw-bold mb-1 text-muted" style={{ fontSize: "0.75rem" }}>{translateSync('Confirm')}</label>
                                                    <input
                                                        className='form-control rounded-0 py-2 px-3'
                                                        name="confirmPassword"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder={translateSync('Retype password')}
                                                        type="password"
                                                        style={{ border: "1px solid #ddd", fontSize: "0.9rem" }}
                                                    />
                                                    {errors.confirmPassword && touched.confirmPassword && <div className="text-danger small mt-1 text-start">{errors.confirmPassword}</div>}
                                                </div>

                                                <div className="col-12 mt-4">
                                                    <div className="form-check d-flex align-items-center">
                                                        <input
                                                            className="form-check-input rounded-0 me-2"
                                                            type="checkbox"
                                                            id="acceptTerms"
                                                            name="acceptTerms"
                                                            checked={values.acceptTerms}
                                                            onChange={handleChange}
                                                            style={{ cursor: "pointer" }}
                                                        />
                                                        <label className="form-check-label small text-muted" htmlFor="acceptTerms">
                                                            {translateSync('I agree to the')} <span className="text-decoration-underline text-dark" style={{ cursor: "pointer" }} onClick={() => navigate('')}>{translateSync('Terms of Service')}</span> & <span className="text-decoration-underline text-dark" style={{ cursor: "pointer" }} onClick={() => navigate('')}>{translateSync('Privacy Policy')}</span>.
                                                        </label>
                                                    </div>
                                                    {errors.acceptTerms && touched.acceptTerms && <div className="text-danger small mt-1">{errors.acceptTerms}</div>}
                                                </div>

                                                <div className="mt-4">
                                                    <button type="submit" className="btn btn-dark w-100 rounded-0 py-2 text-uppercase fw-bold" style={{ letterSpacing: "1px" }}>
                                                        {translateSync('Sign Up')}
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="text-center mt-4 pt-3 border-top">
                                                <p className="small text-muted mb-0">
                                                    {translateSync('Already have an account?')}
                                                    <span
                                                        className="fw-bold ms-1 text-dark text-uppercase cursor-pointer"
                                                        style={{ cursor: "pointer", textDecoration: "underline" }}
                                                        onClick={() => navigate("/login")}
                                                    >
                                                        {translateSync('Log In')}
                                                    </span>
                                                </p>
                                            </div>

                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default RegisterPage;